/**
 * @license Copyright (c) 2003-2021, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

/**
 * @module glossary/utils
 */

const ATTRIBUTE_WHITESPACES =
	/[\u0000-\u0020\u00A0\u1680\u180E\u2000-\u2029\u205f\u3000]/g; // eslint-disable-line no-control-regex

/**
 * A keystroke used by the {@link module:glossary/glossaryui~GlossaryUI glossary UI feature}.
 */
export const GLOSSARY_KEYSTROKE = 'Ctrl+G';

/**
 * Returns `true` if a given view node is the glossary element.
 *
 * @param {module:engine/view/node~Node} node
 * @returns {Boolean}
 */
export function isGlossaryElement( node ) {
	return node.is( 'attributeElement' ) && !!node.getCustomProperty( 'glossary' );
}

/**
 * Creates a glossary {@link module:engine/view/attributeelement~AttributeElement} with the provided `href` attribute.
 *
 * @param {String} href
 * @param {module:engine/conversion/downcastdispatcher~DowncastConversionApi} conversionApi
 * @returns {module:engine/view/attributeelement~AttributeElement}
 */
export function createGlossaryElement( dataGlossary, { writer } ) {
	// Priority 5 - https://github.com/ckeditor/ckeditor5-glossary/issues/121.
	const glossaryElement = writer.createAttributeElement(
		'span',
		{ 'data-glossary': dataGlossary },
		{ priority: 5 }
	);
	writer.setCustomProperty( 'glossary', true, glossaryElement );

	return glossaryElement;
}

/**
 * Returns a safe Definition based on a given value.
 *
 * A Definition is considered safe if it is safe for the user (does not contain any malicious code).
 *
 * If a Definition is considered unsafe, a simple `"#"` is returned.
 *
 * @protected
 * @param {*} definition
 * @returns {String} Safe Definition.
 */
export function ensureSafeDefinition( definition ) {
	definition = String( definition );

	return isSafeDefinition( definition ) ? definition : '#';
}

// Checks whether the given Definition is safe for the user (does not contain any malicious code).
//
// @param {String} definition Definition to check.
function isSafeDefinition( definition ) {
	const normalizedDefinition = definition.replace( ATTRIBUTE_WHITESPACES, '' );

	return normalizedDefinition;
}

/**
 * Returns `true` if the specified `element` is an image and it can be glossaryed (the element allows
 * having the `glossaryDefinition` attribute).
 *
 * @params {module:engine/model/element~Element|null} element
 * @params {module:engine/model/schema~Schema} schema
 * @returns {Boolean}
 */
export function isImageAllowed( element, schema ) {
	if ( !element ) {
		return false;
	}

	return (
		element.is( 'element', 'image' ) &&
		schema.checkAttribute( 'image', 'glossaryDefinition' )
	);
}
